import React, { useContext, useEffect, useState } from 'react'
import { styled } from 'linaria/react'
import ProductPriceInfo from './ProductPriceInfo'
import { theme } from '../../Theme'
import ProductAddToCart from './ProductAddToCart'
import ProductStockStatus from './ProductStockStatus'
import UseFormatProduct from '../../Core/Hooks/UseFormatProduct'
import ProductWalleyDialog from './ProductWalleyDialog'
import { useIntl } from '@jetshop/intl'
// import VariantDropdown from './VariantDropdown'
import { SiteContext } from '../../Global/SiteContext'
import StarIcon from '../../Core/Icons/StarIcon'
import ProductRelatedProducts from './ProductRelatedProducts'
import { useVideoly } from '../../Videoly/useVideoly'
import { VideolyMeta } from '../../Videoly/Meta'
import ProductEdgeFinans from './ProductEdgeFinans'
import ProductPageDetailSkeleton from '../../Core/Skeletons/ProductPageDetailSkeleton'
import { Above } from '@jetshop/ui/Breakpoints'
import ProductPackageProducts from './ProductPackageProducts'
import WhishlistButtonAlt from '../../Core/Buttons/WishlistButtonAlt'
import VariantList from './VariantList'
import {
   PRODUCT_ADD_TO_CART_MOBILE_ID,
   STOCK_ID,
} from '../../Core/Util/Constants'
import ChannelContext from '@jetshop/core/components/ChannelContext'
import GeneralProductUSP from './GeneralProductUSP'
import PreOrderDate from './ProductDetails/PreOrderDate'
import BrandLogoHandler from './ProductDetails/BrandLogoHandler'
import {
   getPackageStockStatus,
   getShippingEstimateMessage,
} from '../../Core/Util/Helpers'

const ProductDetailsContainer = styled.div`
   ${theme.above.lg} {
      padding-left: 4rem;
   }

   .product-details-sku {
      width: fit-content;
      font-family: var(--font-industry);
      border-bottom: 4px solid var(--color-vb-blue);
      font-size: 12px;
   }

   .rating-text {
      font-size: 10px;
      font-weight: bold;
      margin-top: 0.25rem;
   }

   .star-icon-container {
      cursor: pointer;
      min-height: 2.5rem;
   }

   .star-icon-container svg {
      height: 15px;
   }

   .product-details-name {
      font-family: var(--font-industry);
      font-weight: 700;
      font-size: ${theme.font.size.responsiveHeaderM};
      ${theme.above.lg} {
         font-size: ${theme.font.size.responsiveHeaderS};
      }
      word-wrap: break-word;
   }

   .product-sidebar-title {
      font-family: var(--font-industry);
      font-size: ${theme.font.size.responsiveHeaderM};
      ${theme.above.lg} {
         font-size: 24px;
      }
   }

   .product-short-desc p {
      font-size: 14px;
   }

   .back-in-stock-text {
      font-size: 12px;
      font-family: var(--font-industry);
      font-weight: bold;
   }

   .add-to-cart-container {
      margin-top: 0.75rem;
   }

   // Override imported script in ProductEdgeFinans.js
   #edgecontainer {
      float: none !important;
   }

   .wishlist-btn-wrapper {
      position: absolute;
      right: 0;
      top: -17px;
      ${theme.below.sm} {
         top: -14px;
         overflow: hidden;
      }
   }

   .wishlist-btn-wrapper .heart-animation {
      ${theme.below.sm} {
         height: 40px;
         width: 40px;
      }
   }

   .shipping-estimate-message {
      font-size: 1rem;
      font-weight: 700;
      font-family: var(--font-industry);
   }
`

export const getShippingEstimateInfo = ({
   activeVariant,
   packageProducts,
   selectedChannelId,
}) => {
   const isMainVariantPackage = activeVariant?.isPackage === true

   const hasPackageItems =
      isMainVariantPackage &&
      Array.isArray(packageProducts) &&
      packageProducts.length > 0

   const packageStatus =
      hasPackageItems && activeVariant?.stockStatus?.buyable === true
         ? getPackageStockStatus(packageProducts)
         : { stockStatusId: 3 } // default to out of stock

   const effectiveStockStatusId = isMainVariantPackage
      ? packageStatus?.stockStatusId
      : activeVariant?.stockStatus?.stockStatusId

   // Only show shipping estimate message for Sweden
   const showShippingEstimateMessage =
      STOCK_ID.IN_STOCK.includes(effectiveStockStatusId) &&
      !STOCK_ID.PREBOOK.includes(effectiveStockStatusId) &&
      selectedChannelId == 1

   const shippingEstimateMessage = showShippingEstimateMessage ? getShippingEstimateMessage(selectedChannelId, false) : ''

   return {
      showShippingEstimateMessage,
      shippingEstimateMessage,
   }
}

const ProductDetails = ({ productData, brandCat, productSettings }) => {
   const t = useIntl()
   const { selectedChannel } = useContext(ChannelContext)
   const { setReviewsAccOpen, productInformation } = useContext(SiteContext)
   const [isLoading, setIsLoading] = useState(true)
   const { activeVariant, productReviews, packageProducts } =
      useContext(SiteContext)

   const { showShippingEstimateMessage, shippingEstimateMessage } =
   getShippingEstimateInfo({
         activeVariant,
         packageProducts,
         selectedChannelId: selectedChannel?.id,
      })

   // Only show warehouse-stockstatus if on swedish channel
   const showWareHouseStock = selectedChannel?.id == 1

   const brandLogo = {
      src: brandCat?.images[0]?.url,
      alt: activeVariant?.brandName
         ? `${activeVariant?.brandName}-logo`
         : 'logo',
   }

   const brandName = brandCat?.name
   const brandLink = brandCat?.primaryRoute?.path

   const { videos } = useVideoly(productData)

   function handleScrollToSection(sectionId, openReviewsAccordion) {
      setReviewsAccOpen(true)
      const section = document.getElementById(sectionId)
      if (!section) {
         return
      }
      window.scrollTo({
         behavior: 'smooth',
         left: 0,
         top: section.offsetTop + 110, // Adjust the offset as needed
      })
   }

   useEffect(() => {
      if (productData != null) {
         setIsLoading(false)
      }
   }, [productData])

   if (isLoading) {
      return <ProductPageDetailSkeleton></ProductPageDetailSkeleton>
   }

   const brandLogoAndRating = (
      <div
         className={`d-flex align-items-start ${
            (brandName && brandLink) || brandLogo.src
               ? 'justify-content-between'
               : 'justify-content-end'
         }`}
      >
         <BrandLogoHandler
            brandLogo={brandLogo}
            brandLink={brandLink}
            brandName={brandName}
         />
         <div
            className=" d-flex gap-1 align-items-start star-icon-container"
            onClick={() => handleScrollToSection('product-review')}
         >
            {productReviews && productReviews?.reviews?.length > 0 && (
               <>
                  <StarIcon
                     filled={
                        isNaN(productReviews?.summary?.your_avg_score / 5)
                           ? 0
                           : productReviews?.summary?.your_avg_score / 5
                     }
                  ></StarIcon>

                  <span className="rating-text">
                     {productReviews?.summary?.your_avg_score ?? 0} {t('of')} 5
                     ({productReviews?.summary?.your_review_count ?? 0})
                  </span>
               </>
            )}
         </div>
      </div>
   )

   const articleNumberAndWishlistBtn = (
      <div className="d-flex justify-content-between align-items-center position-relative">
         <p className="product-details-sku mb-8">
            ARTNR: {activeVariant?.articleNumber}
         </p>

         <div className="wishlist-btn-wrapper">
            <WhishlistButtonAlt
               title={activeVariant?.values[0]}
               articleNumber={activeVariant?.articleNumber}
            ></WhishlistButtonAlt>
         </div>
      </div>
   )

   const titleWithPossibleBrandName = (
      <h1 className="product-details-name" title={activeVariant?.title}>
         {activeVariant?.brandName && activeVariant?.title
            ? `${activeVariant.brandName} ${activeVariant.title}`
            : activeVariant?.title}
      </h1>
   )

   return (
      <ProductDetailsContainer>
         <VideolyMeta product={activeVariant}></VideolyMeta>
         {brandLogoAndRating}
         {articleNumberAndWishlistBtn}
         {titleWithPossibleBrandName}
         <ProductPriceInfo
            product={productData}
            selectedVariant={activeVariant}
         ></ProductPriceInfo>

         {activeVariant?.currentPrice && (
            <ProductStockStatus
               activeVariant={activeVariant}
               includeWarehouse={showWareHouseStock}
               packageProducts={packageProducts}
            ></ProductStockStatus>
         )}

         <div className="add-to-cart-container mt-1">
            <PreOrderDate productData={productData}></PreOrderDate>
            <ProductAddToCart
               selectedVariant={activeVariant}
               packageProducts={packageProducts}
               id={PRODUCT_ADD_TO_CART_MOBILE_ID}
               productInformation={productInformation}
            ></ProductAddToCart>
         </div>

         {showShippingEstimateMessage && (
            <div className="mt-16">
               <span className="shipping-estimate-message">
                  {shippingEstimateMessage}
               </span>
            </div>
         )}

         <GeneralProductUSP
            productSettings={productSettings}
            className={showShippingEstimateMessage ? 'mt-8' : 'mt-16'}
         />

         {/* {!hideProductUSP && (
            <div
               className={cx('mt-16', cartButtonWrapper)}
               dangerouslySetInnerHTML={{
                  __html: uspContent,
               }}
            />
         )} */}

         {activeVariant?.hasVariants && (
            <VariantList
               productData={productData}
               selectedVariant={activeVariant}
               options={productData?.options}
            ></VariantList>
         )}

         {packageProducts && (
            <ProductPackageProducts
               packageProducts={packageProducts}
               className="my-16"
            />
         )}
         <ProductWalleyDialog
            selectedVariant={activeVariant}
         ></ProductWalleyDialog>

         <ProductEdgeFinans></ProductEdgeFinans>

         <Above breakpoint="lg">
            <ProductRelatedProducts></ProductRelatedProducts>
         </Above>
      </ProductDetailsContainer>
   )
}

export default ProductDetails
